import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { supabase } from '@/integrations/supabase/client'
import { Copy, Check, Loader2, Trash2 } from 'lucide-react'
import { PageHeader } from '@/components/ui/PageHeader'

interface Profile {
    id: string
    user_id: string | null
    email: string
    worker_name: string | null
}

interface Counts {
    notes: number
    stats: number
    notifications: number
}

interface ClearTestDataProps {
    onNavigateHome: () => void
}

export const ClearTestData = ({ onNavigateHome }: ClearTestDataProps) => {
    const [profiles, setProfiles] = useState<Profile[]>([])
    const [selected, setSelected] = useState<Set<string>>(new Set())
    const [counts, setCounts] = useState<Counts | null>(null)
    const [loadingProfiles, setLoadingProfiles] = useState(true)
    const [loadingCount, setLoadingCount] = useState(false)
    const [copied, setCopied] = useState(false)

    useEffect(() => {
        const fetchProfiles = async () => {
            const { data } = await supabase
                .from('profiles')
                .select('id, user_id, email, workers(name)')
                .is('deleted_dt', null)
                .order('email')
            if (data) {
                setProfiles(data.map((p: any) => ({
                    id: p.id,
                    user_id: p.user_id ?? null,
                    email: p.email,
                    worker_name: p.workers?.name ?? null,
                })))
            }
            setLoadingProfiles(false)
        }
        fetchProfiles()
    }, [])

    const toggleProfile = (id: string) => {
        setSelected(prev => {
            const next = new Set(prev)
            if (next.has(id)) next.delete(id)
            else next.add(id)
            return next
        })
        setCounts(null)
    }

    const toggleAll = () => {
        if (selected.size === profiles.length) setSelected(new Set())
        else setSelected(new Set(profiles.map(p => p.id)))
        setCounts(null)
    }

    const handleCountRecords = async () => {
        if (selected.size === 0) return
        setLoadingCount(true)
        const selectedProfiles = profiles.filter(p => selected.has(p.id))
        const profileIds = selectedProfiles.map(p => p.id)
        const userIds = selectedProfiles.map(p => p.user_id).filter(Boolean) as string[]

        const [nt, st, no] = await Promise.all([
            userIds.length
                ? supabase.from('notes').select('id', { count: 'exact', head: true }).in('created_by', userIds)
                : Promise.resolve({ count: 0 }),
            userIds.length
                ? supabase.from('stats').select('id', { count: 'exact', head: true }).in('created_by', userIds)
                : Promise.resolve({ count: 0 }),
            supabase.from('notifications').select('id', { count: 'exact', head: true }).in('created_by', profileIds),
        ])
        setCounts({
            notes: (nt as any).count ?? 0,
            stats: (st as any).count ?? 0,
            notifications: (no as any).count ?? 0,
        })
        setLoadingCount(false)
    }

    const generateSql = (): string => {
        const ids = Array.from(selected)
        const selectedProfiles = profiles.filter(p => ids.includes(p.id))
        const labels = selectedProfiles.map(p => p.worker_name ? `${p.worker_name} (${p.email})` : p.email)
        const userIdList = selectedProfiles.map(p => `'${p.user_id}'`).join(', ')
        const profileIdList = ids.map(id => `'${id}'`).join(', ')

        return `-- ⚠️  DELETE test data for: ${labels.join(', ')}
-- Run this manually in Supabase SQL Editor

-- notes & stats: created_by = auth.users.id (= profiles.user_id)
DELETE FROM notes WHERE created_by IN (${userIdList});
DELETE FROM stats WHERE created_by IN (${userIdList});

-- notifications: created_by = profiles.id
DELETE FROM notifications WHERE created_by IN (${profileIdList});`
    }

    const handleCopy = async () => {
        await navigator.clipboard.writeText(generateSql())
        setCopied(true)
        setTimeout(() => setCopied(false), 2000)
    }

    return (
        <div className="min-h-screen bg-gray-50">
            <PageHeader title="Clear Test Data" icon={<Trash2 size={20} />} onBack={onNavigateHome} />

            <main className="max-w-2xl mx-auto py-8 px-4 space-y-6">

                {/* Profile selection */}
                <div className="bg-white rounded-lg shadow p-6">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="font-semibold text-gray-800">Sélectionner les profils</h2>
                        {!loadingProfiles && profiles.length > 0 && (
                            <button onClick={toggleAll} className="text-xs text-blue-600 hover:underline">
                                {selected.size === profiles.length ? 'Tout déselectionner' : 'Tout sélectionner'}
                            </button>
                        )}
                    </div>

                    {loadingProfiles ? (
                        <div className="flex items-center gap-2 text-gray-500 text-sm">
                            <Loader2 size={14} className="animate-spin" /> Chargement...
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {profiles.map(p => (
                                <label key={p.id} className="flex items-center gap-3 p-2 rounded hover:bg-gray-50 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={selected.has(p.id)}
                                        onChange={() => toggleProfile(p.id)}
                                        className="h-4 w-4 rounded border-gray-300 text-blue-600"
                                    />
                                    <div>
                                        <p className="text-sm font-medium text-gray-800">{p.worker_name ?? '—'}</p>
                                        <p className="text-xs text-gray-400">{p.email}</p>
                                    </div>
                                </label>
                            ))}
                        </div>
                    )}
                </div>

                {/* SQL + count */}
                {selected.size > 0 && (
                    <div className="bg-white rounded-lg shadow p-6 space-y-4">
                        <p className="text-sm text-gray-600">
                            {selected.size} profil{selected.size > 1 ? 's' : ''} sélectionné{selected.size > 1 ? 's' : ''}
                        </p>

                        <div className="flex items-center justify-between">
                            <Button
                                onClick={handleCountRecords}
                                disabled={loadingCount}
                                variant="outline"
                                size="sm"
                                className="flex items-center gap-1.5"
                            >
                                {loadingCount
                                    ? <><Loader2 size={13} className="animate-spin" /> Comptage...</>
                                    : 'Compter les enregistrements'}
                            </Button>
                        </div>

                        {counts && (
                            <div className="grid grid-cols-3 gap-3">
                                {[
                                    { label: 'Notes', value: counts.notes },
                                    { label: 'Stats', value: counts.stats },
                                    { label: 'Notifications', value: counts.notifications },
                                ].map(({ label, value }) => (
                                    <div key={label} className="bg-gray-50 rounded-lg p-3 text-center">
                                        <p className="text-2xl font-bold text-red-500">{value}</p>
                                        <p className="text-xs text-gray-500 mt-0.5">{label}</p>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* SQL block */}
                        <div>
                            <div className="flex items-center justify-between mb-2">
                                <p className="text-sm font-medium text-gray-700">
                                    Requête SQL → Supabase SQL Editor
                                </p>
                                <Button onClick={handleCopy} size="sm" variant="outline" className="flex items-center gap-1.5">
                                    {copied
                                        ? <><Check size={13} className="text-green-500" /> Copié !</>
                                        : <><Copy size={13} /> Copier</>}
                                </Button>
                            </div>
                            <pre className="bg-gray-900 text-green-400 text-xs rounded-lg p-4 overflow-x-auto whitespace-pre-wrap break-all">
                                {generateSql()}
                            </pre>
                        </div>

                        <p className="text-xs text-red-500 font-medium">
                            ⚠️ Cette requête supprime définitivement les données. Exécute-la manuellement dans le SQL Editor de Supabase.
                        </p>
                    </div>
                )}
            </main>
        </div>
    )
}
